import CalendarSharingDeliveryOption from '../store/schema/CalendarSharingDeliveryOption';
import CalendarSharingError from '../store/schema/CalendarSharingError';
import SharingPermissionItemState from '../store/schema/SharingPermissionItemState';
import calendarStrings from 'owa-strings/owa-calendar-strings';

export const CalendarSharingDetailLevels = [
    'Disabled',
    'AvailabilityOnly',
    'LimitedDetails',
    'FullDetails',
    'Editor',
    'Delegate',
    'Custom',
];

/**
 * Schema for Recipient permission dropdown
 */
export interface CalendarSharingAllowablePermission {
    key: string;
    text: string;
}

/**
 * Returns list of allowable permissions object to display in dropdown for a recipient.
 */
export function getCalendarSharingAllowablePermissions(
    allowablePermissions: string[]
): CalendarSharingAllowablePermission[] {
    return allowablePermissions.map(permission => {
        return { key: permission, text: getCalendarSharingPermissions(permission) };
    });
}

/**
 * Schema for Delegate permission dropdown
 */
export interface CalendarSharingDelegatePermission {
    key: number;
    text: string;
}

/**
 * Returns list of Calendar Sharing permissions text for dropdown.
 */
export function getCalendarSharingPermissions(permission: string): string {
    switch (permission) {
        case 'AvailabilityOnly': {
            return calendarStrings.canViewBusyPermission;
        }
        case 'LimitedDetails': {
            return calendarStrings.canViewTitlesPermission;
        }
        case 'FullDetails': {
            return calendarStrings.canViewAllDetailsPermission;
        }
        case 'Editor': {
            return calendarStrings.canEditPermission;
        }
        case 'Delegate': {
            return calendarStrings.delegatePermission;
        }
        case 'Disabled': {
            return calendarStrings.notSharedPermission;
        }
        default: {
            return '';
        }
    }
}

/*
 * Returns list of delegate permissions to be displayed.
 */
export function getCalendarSharingDelegatePermissions(): CalendarSharingDelegatePermission[] {
    return [
        {
            key: CalendarSharingDeliveryOption.DelegatesOnly,
            text: calendarStrings.delegateOnlyPermission,
        },
        {
            key: CalendarSharingDeliveryOption.DelegatesAndSendInformationToMe,
            text: calendarStrings.delegateAndSendMeCopyPermission,
        },
        {
            key: CalendarSharingDeliveryOption.DelegatesAndMe,
            text: calendarStrings.delegateAndMePermission,
        },
    ];
}

/**
 * Returns the tooltip string for Calendar Sharing permission.
 */
export function getCalendarSharingPermissionsDropdownTooltip(permission: string): string {
    switch (permission) {
        case 'AvailabilityOnly': {
            return calendarStrings.canViewBusyPermissionTooltip;
        }
        case 'LimitedDetails': {
            return calendarStrings.canViewTitlesPermissionTooltip;
        }
        case 'FullDetails': {
            return calendarStrings.canViewAllDetailsPermissionTooltip;
        }
        case 'Editor': {
            return calendarStrings.canEditPermissionTooltip;
        }
        case 'Delegate': {
            return calendarStrings.delegatePermissionTooltip;
        }
        case 'Disabled': {
            return calendarStrings.notSharedPermissionTooltip;
        }
        default: {
            return '';
        }
    }
}

/**
 * Returns the tooltip string for Calendar Sharing delegate permission.
 */
export function getCalendarSharingDelegatePermissionsDropdownTooltip(
    permission: CalendarSharingDeliveryOption
): string {
    switch (permission) {
        case CalendarSharingDeliveryOption.DelegatesOnly: {
            return calendarStrings.delegateOnlyPermissionTooltip;
        }
        case CalendarSharingDeliveryOption.DelegatesAndMe: {
            return calendarStrings.delegateAndMePermissionTooltip;
        }
        case CalendarSharingDeliveryOption.DelegatesAndSendInformationToMe: {
            return calendarStrings.delegateAndSendMeCopyPermissionTooltip;
        }
        default: {
            return '';
        }
    }
}

/**
 * Returns true if sharing permission vlaue is delegate.
 */
export function isDelegate(sharingPermissionItem: SharingPermissionItemState) {
    return sharingPermissionItem.CurrentDetailLevel == 'Delegate';
}

/**
 * Returns the string for Calendar Sharing errors.
 */
export function getCalendarSharingError(errorStatus: CalendarSharingError): string {
    switch (errorStatus) {
        case CalendarSharingError.InitialLoadFailure: {
            return calendarStrings.sharingPermissionsNotLoaded;
        }
        case CalendarSharingError.EmailValidationFailure: {
            return calendarStrings.emailAddressValidationFailed;
        }
        case CalendarSharingError.SendInviteFailure: {
            return calendarStrings.sharingInviteSendFailed;
        }
        case CalendarSharingError.SharingPermissionUpdateFailure: {
            return calendarStrings.sharingPermissionUpdateFailed;
        }
        case CalendarSharingError.SharingPermissionDeleteFailure: {
            return calendarStrings.sharingPermissionDeleteFailed;
        }
        default: {
            return '';
        }
    }
}
